import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { inventoryAPI, mrpAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Plus, Trash2, Play, Download, Upload, Loader2, FileSpreadsheet, ChevronRight, Layers, FileOutput, ShoppingCart, Copy } from 'lucide-react';

export default function MRPPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [lines, setLines] = useState([{ item_id: '', quantity: 1 }]);
  const [planName, setPlanName] = useState('');
  const [cycle, setCycle] = useState('');
  const [requiredDate, setRequiredDate] = useState('');
  const [orderMode, setOrderMode] = useState('full_sheet');  // 'full_sheet' | 'cts'
  const [ignoreFgStock, setIgnoreFgStock] = useState(false);
  const [ignoreRmStock, setIgnoreRmStock] = useState(false);
  const [editingRunId, setEditingRunId] = useState(null);   // when set, Run MRP overwrites this saved plan
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [runId, setRunId] = useState(null);
  const [runs, setRuns] = useState([]);
  const [importing, setImporting] = useState(false);
  const [routesByItem, setRoutesByItem] = useState({});
  const [overrides, setOverrides] = useState({});   // item_id -> route_id (per-level BOM override)
  const [exporting, setExporting] = useState(false);
  const [converting, setConverting] = useState(false);
  const [confirmConvert, setConfirmConvert] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedProcure, setSelectedProcure] = useState({});   // item_id -> bool

  // Procurement lines available to convert (positive to-procure, not yet indented)
  const procurableLines = React.useMemo(() => {
    if (!result) return [];
    const rm = (result.raw_materials || []).filter(r => (r.to_procure_sheets || 0) > 0);
    const pu = (result.purchased || []).filter(p => (p.to_procure_qty || 0) > 0);
    return [...rm, ...pu].map(x => ({ item_id: x.item_id, indented: !!x.indented }));
  }, [result]);

  // Reset selection whenever the result changes: pre-select all not-yet-indented lines.
  useEffect(() => {
    const next = {};
    procurableLines.forEach(l => { if (!l.indented) next[l.item_id] = true; });
    setSelectedProcure(next);
  }, [procurableLines]);

  const selectedIds = Object.keys(selectedProcure).filter(k => selectedProcure[k]);
  const toggleProcure = (itemId) => setSelectedProcure(s => ({ ...s, [itemId]: !s[itemId] }));

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [itemsRes, cyclesRes, runsRes, routesRes] = await Promise.all([
        inventoryAPI.listItems({ limit: 1000, sort_by: 'item_name', sort_order: 'asc' }),
        mrpAPI.planningCycles(),
        mrpAPI.listRuns(),
        inventoryAPI.listRoutes({ limit: 1000 }),
      ]);
      setItems(itemsRes.data.items || []);
      setCycles(cyclesRes.data || []);
      setRuns(runsRes.data || []);
      const map = {};
      (routesRes.data.routes || []).forEach(r => {
        (map[r.item_id] = map[r.item_id] || []).push({ id: r.id, route_name: r.route_name, is_default: r.is_default });
      });
      setRoutesByItem(map);
    } catch (err) {
      toast.error('Failed to load MRP data');
    }
  };

  // Only sellable/makeable parts are valid top-level MRP inputs
  const planItems = items.filter(i =>
    i.item_type === 'Finished Good' || i.item_type === 'Sheet Metal Child Part' || i.item_type === 'Semi-Finished Part'
  );

  const addLine = () => setLines(l => [...l, { item_id: '', quantity: 1 }]);
  const removeLine = (idx) => setLines(l => l.filter((_, i) => i !== idx));
  const updateLine = (idx, field, value) => setLines(l => l.map((row, i) => i === idx ? { ...row, [field]: value } : row));

  const resetForm = () => {
    setEditingRunId(null); setPlanName(''); setCycle(''); setRequiredDate('');
    setOrderMode('full_sheet'); setOverrides({}); setLines([{ item_id: '', quantity: 1 }]);
    setResult(null); setRunId(null);
  };

  const handleRun = async (overrideMap = overrides) => {
    const valid = lines.filter(l => l.item_id && parseFloat(l.quantity) > 0);
    if (valid.length === 0) { toast.error('Add at least one part with a quantity'); return; }
    setRunning(true);
    try {
      const payload = {
        name: planName,
        planning_cycle: cycle,
        required_date: requiredDate || '',
        order_mode: orderMode,
        ignore_fg_stock: ignoreFgStock,
        ignore_rm_stock: ignoreRmStock,
        lines: valid.map(l => ({ item_id: l.item_id, quantity: parseFloat(l.quantity) })),
        bom_overrides: overrideMap,
        save: true,
      };
      // When editing an existing plan, re-run overwrites the SAME saved plan.
      const res = editingRunId
        ? await mrpAPI.rerun(editingRunId, payload)
        : await mrpAPI.run(payload);
      setResult(res.data.result ? { ...res.data.result, name: res.data.name } : res.data);
      setRunId(res.data.id || editingRunId || null);
      setEditingRunId(res.data.id || editingRunId || null);
      if (res.data.name && !planName) setPlanName(res.data.name);
      toast.success(editingRunId ? 'MRP plan re-run & updated' : 'MRP run complete');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'MRP run failed');
    } finally { setRunning(false); }
  };

  const handleOverride = (itemId, routeId) => {
    const next = { ...overrides };
    if (routeId) next[itemId] = routeId; else delete next[itemId];
    setOverrides(next);
    handleRun(next);   // re-explode with the chosen BOM at this node
  };

  const handleTemplate = async () => {
    try {
      const res = await mrpAPI.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'mrp_bulk_template.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Could not download template'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await mrpAPI.runExcel(fd, { name: planName || 'Bulk MRP', planning_cycle: cycle, save: true });
      if (res.data.import_errors?.length) toast.warning(`${res.data.import_errors.length} row(s) skipped`);
      setResult(res.data.result ? { ...res.data.result, name: res.data.name } : res.data);
      setRunId(res.data.id || null);
      setEditingRunId(res.data.id || null);
      toast.success('Bulk MRP complete');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk MRP failed');
    } finally { setImporting(false); }
  };

  const openRun = async (id) => {
    try {
      const res = await mrpAPI.getRun(id);
      const r = res.data;
      // Auto-populate the Planning Inputs so the plan can be edited & re-run in place.
      setOverrides(r.bom_overrides || {});
      setOrderMode(r.order_mode || 'full_sheet');
      setIgnoreFgStock(!!r.ignore_fg_stock);
      setIgnoreRmStock(!!r.ignore_rm_stock);
      setPlanName(r.name || '');
      setCycle(r.planning_cycle || '');
      setRequiredDate(r.required_date || '');
      setLines((r.lines && r.lines.length)
        ? r.lines.map(l => ({ item_id: l.item_id, quantity: l.quantity }))
        : [{ item_id: '', quantity: 1 }]);
      setEditingRunId(id);
      setRunId(id);
      setResult(r.result ? { ...r.result, name: r.name } : r);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch { toast.error('Failed to load run'); }
  };

  // Copy an existing plan's inputs into the form as a NEW plan (does not touch the original).
  const copyRun = async (id) => {
    try {
      const res = await mrpAPI.getRun(id);
      const r = res.data;
      setOverrides(r.bom_overrides || {});
      setOrderMode(r.order_mode || 'full_sheet');
      setIgnoreFgStock(!!r.ignore_fg_stock);
      setIgnoreRmStock(!!r.ignore_rm_stock);
      setPlanName(`${r.name || 'MRP Plan'} (Copy)`);
      setCycle(r.planning_cycle || '');
      setRequiredDate('');   // new plan — planner sets a fresh required date
      setLines((r.lines && r.lines.length)
        ? r.lines.map(l => ({ item_id: l.item_id, quantity: l.quantity }))
        : [{ item_id: '', quantity: 1 }]);
      setEditingRunId(null);   // NEW plan: running will create a fresh saved plan
      setRunId(null);
      setResult(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.info('Copied plan inputs — set a date and press Run MRP to save as a new plan');
    } catch { toast.error('Failed to copy run'); }
  };

  const handleExport = async () => {
    if (!runId) { toast.error('Save/open a plan first to export'); return; }
    setExporting(true);
    try {
      const res = await mrpAPI.exportRun(runId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `${(result?.name || 'mrp_run').replace(/[^a-z0-9 \-_]/gi, '_')}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Could not export MRP run'); } finally { setExporting(false); }
  };

  const handleConvertToIndent = async (deleteRun = true) => {
    if (!runId) return;
    if (selectedIds.length === 0) { toast.error('Select at least one line to convert'); return; }
    setConverting(true);
    try {
      const res = await mrpAPI.convertToIndent(runId, selectedIds, deleteRun);
      const { added = 0, updated = 0, skipped = [], run_deleted, all_converted, remaining = 0 } = res.data;
      toast.success(`Converted to indent: ${added} added, ${updated} updated${skipped.length ? `, ${skipped.length} skipped` : ''}`);
      loadData();
      if (run_deleted) {
        resetForm();
        navigate('/purchase/indent');
      } else {
        if (all_converted) {
          toast.info('Plan kept — delete it manually when you no longer need it');
        } else {
          toast.info(`${remaining} line(s) still to convert in this plan`);
        }
        openRun(runId);   // refresh to show "Indented" marks
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to convert to indent');
    } finally { setConverting(false); setConfirmConvert(false); }
  };

  const handleDeleteRun = async () => {
    if (!deleteTarget) return;
    try {
      await mrpAPI.deleteRun(deleteTarget.id);
      toast.success('MRP run deleted');
      if (runId === deleteTarget.id) { resetForm(); }
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete run');
    } finally { setDeleteTarget(null); }
  };

  const num = (v) => (v == null ? '—' : (Math.round(v * 10000) / 10000).toLocaleString());

  return (
    <div className="space-y-4 max-w-6xl" data-testid="mrp-page">
      <div className="flex items-center gap-2">
        <Layers className="w-5 h-5 text-amber-600" />
        <h1 className="font-heading text-xl font-bold tracking-tight">Material Requirements Planning</h1>
      </div>

      {/* Plan inputs */}
      <Card className={editingRunId ? 'border-amber-400' : ''}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              {editingRunId ? 'Edit Plan' : 'Planning Inputs'}
              {editingRunId && <span className="text-[10px] uppercase tracking-wider bg-amber-500 text-white px-1.5 py-0.5 rounded" data-testid="mrp-editing-badge">Editing</span>}
            </CardTitle>
            <div className="flex items-center gap-2">
              {editingRunId && (
                <Button size="sm" variant="ghost" onClick={resetForm} data-testid="mrp-new-plan-btn">
                  <Plus className="w-4 h-4 mr-1" /> New Plan
                </Button>
              )}
              <input type="file" id="mrp-import" accept=".xlsx,.xlsm" className="hidden" onChange={handleImport} data-testid="mrp-import-input" />
              <Button size="sm" variant="outline" onClick={handleTemplate} data-testid="mrp-template-btn"><Download className="w-4 h-4 mr-1" /> Template</Button>
              <Button size="sm" variant="outline" onClick={() => document.getElementById('mrp-import').click()} disabled={importing} data-testid="mrp-import-btn">
                {importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Bulk Excel
              </Button>
            </div>
          </div>
          {editingRunId && (
            <p className="text-[11px] text-amber-700 mt-1" data-testid="mrp-editing-hint">
              Editing an existing plan — add / edit / remove parts and press <strong>Re-run &amp; Update</strong>. This overwrites the same saved plan with a fresh calculation.
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Plan Name</Label>
              <Input value={planName} onChange={e => setPlanName(e.target.value)} placeholder="e.g. July Plan 1" className="h-8 text-xs" data-testid="mrp-name" />
            </div>
            <div>
              <Label className="text-xs">Planning Cycle</Label>
              <Select value={cycle || '_none'} onValueChange={v => setCycle(v === '_none' ? '' : v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="mrp-cycle"><SelectValue placeholder="Select cycle" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">-- None --</SelectItem>
                  {cycles.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Required by Date</Label>
              <Input type="date" value={requiredDate} onChange={e => setRequiredDate(e.target.value)} className="h-8 text-xs" data-testid="mrp-required-date" />
            </div>
            <div>
              <Label className="text-xs">Order Mode</Label>
              <Select value={orderMode} onValueChange={setOrderMode}>
                <SelectTrigger className="h-8 text-xs" data-testid="mrp-order-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full_sheet">Full Standard Sheet</SelectItem>
                  <SelectItem value="cts">Cut-to-Size (CTS)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-0.5">CTS shows strip sizes, strips & sheets to cut.</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded border bg-muted/20 p-2.5">
            <span className="text-xs font-medium text-muted-foreground">Stock handling:</span>
            <label className="flex items-center gap-2 cursor-pointer text-xs" data-testid="mrp-ignore-fg-toggle">
              <Checkbox checked={ignoreFgStock} onCheckedChange={v => setIgnoreFgStock(!!v)} />
              <span>Ignore finished-goods stock <span className="text-muted-foreground">(top-level planned parts)</span></span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-xs" data-testid="mrp-ignore-rm-toggle">
              <Checkbox checked={ignoreRmStock} onCheckedChange={v => setIgnoreRmStock(!!v)} />
              <span>Exclude raw-material / component stock <span className="text-muted-foreground">(all exploded children)</span></span>
            </label>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Parts to Plan</Label>
            {lines.map((line, idx) => (
              <div key={idx} className="flex items-center gap-2" data-testid={`mrp-line-${idx}`}>
                <div className="flex-1">
                  <SearchableSelect
                    options={planItems.map(i => ({ value: i.id, label: `${i.item_name} — ${i.unique_id}`, searchStr: `${i.item_name} ${i.unique_id}` }))}
                    value={line.item_id}
                    onSelect={v => updateLine(idx, 'item_id', v)}
                    placeholder="Select a part…"
                    testId={`mrp-item-${idx}`}
                  />
                </div>
                <Input type="number" min="0" step="any" value={line.quantity} onChange={e => updateLine(idx, 'quantity', e.target.value)}
                  className="h-8 text-xs w-28" placeholder="Qty" data-testid={`mrp-qty-${idx}`} />
                <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeLine(idx)} disabled={lines.length === 1}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="mrp-add-line"><Plus className="w-4 h-4 mr-1" /> Add Part</Button>
          </div>

          <div className="flex justify-end">
            <Button onClick={() => handleRun()} disabled={running} data-testid="mrp-run-btn">
              {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />} {editingRunId ? 'Re-run & Update' : 'Run MRP'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <div className="space-y-4" data-testid="mrp-result">
          <Card className="border-amber-200">
            <CardContent className="py-3 flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-sm font-semibold">{result.name || 'MRP Result'}</div>
                <div className="text-[11px] text-muted-foreground">
                  {num(result.summary?.total_rm_sheets)} sheets · {num(result.summary?.total_rm_weight_kg)} Kg · {(result.purchased || []).length} other purchased items
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={handleExport} disabled={exporting || !runId} data-testid="mrp-export-btn">
                  {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileOutput className="w-4 h-4 mr-1" />} Export Excel
                </Button>
                <Button size="sm" onClick={() => setConfirmConvert(true)} disabled={converting || !runId || selectedIds.length === 0} data-testid="mrp-convert-btn">
                  {converting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ShoppingCart className="w-4 h-4 mr-1" />} Convert to Indent{selectedIds.length ? ` (${selectedIds.length})` : ''}
                </Button>
              </div>
            </CardContent>
          </Card>
          {orderMode === 'cts' && (result.cts_breakdown || []).length > 0 && (
            <Card className="border-amber-200" data-testid="mrp-cts-card">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Cut-to-Size (CTS) Cutting Plan</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-left border-b text-muted-foreground">
                      <th className="py-1.5 pr-2">Part</th>
                      <th className="pr-2">RM Sheet</th>
                      <th className="pr-2">CTS Size (mm)</th>
                      <th className="pr-2 text-right">Blanks/Strip</th>
                      <th className="pr-2 text-right">Strips/Sheet</th>
                      <th className="pr-2 text-right">Parts/Sheet</th>
                      <th className="pr-2 text-right">Std Sheets</th>
                      <th className="pr-2 text-right">CTS Strips</th>
                      <th className="pr-2 text-right">Parts Obtained</th>
                      <th className="pr-2 text-right">Extra</th>
                      <th className="pr-2 text-right">Scrap (Kg)</th>
                    </tr></thead>
                    <tbody>
                      {result.cts_breakdown.map((c, i) => {
                        const n = (result.nodes || []).find(x => x.cts && x.cts.rm_item_id === c.rm_item_id && x.cts.cts_size === c.cts_size) || {};
                        return (
                        <tr key={i} className="border-b" data-testid={`mrp-cts-row-${i}`}>
                          <td className="py-1.5 pr-2 font-medium">{n.item_name || '—'}<div className="text-[10px] text-muted-foreground font-mono">{n.unique_id || ''}</div></td>
                          <td className="pr-2">{c.rm_name}<div className="text-[10px] text-muted-foreground font-mono">{c.grade} · {c.thickness}mm · {c.sheet_size}</div></td>
                          <td className="pr-2 font-medium text-amber-700">{c.cts_size}</td>
                          <td className="pr-2 text-right font-mono">{num(c.blanks_per_strip)}</td>
                          <td className="pr-2 text-right font-mono">{num(c.strips_per_sheet)}</td>
                          <td className="pr-2 text-right font-mono">{num(c.parts_per_sheet)}</td>
                          <td className="pr-2 text-right font-mono font-bold text-emerald-700">{num(c.sheets_needed)}</td>
                          <td className="pr-2 text-right font-mono font-bold">{num(c.total_strips)}</td>
                          <td className="pr-2 text-right font-mono">{num(c.parts_obtained)}</td>
                          <td className="pr-2 text-right font-mono text-muted-foreground">+{num(c.extra_parts)}</td>
                          <td className="pr-2 text-right font-mono text-amber-700">{num(c.scrap_kg)}</td>
                        </tr>
                      );})}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">Order <strong>Std Sheets</strong> standard sheets, cut into the listed <strong>CTS Size</strong> strips. "Parts Obtained" may exceed demand (whole-sheet rounding); "Extra" is the surplus.</p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Raw Material Requirements (Sheet Metal)</CardTitle></CardHeader>
            <CardContent>
              {(result.raw_materials || []).length === 0 ? (
                <p className="text-xs text-muted-foreground py-4 text-center">No sheet-metal raw material required (check stock / BOMs).</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left border-b text-muted-foreground">
                        <th className="py-1.5 pr-2 w-8"></th>
                        <th className="py-1.5 pr-2">Material</th>
                        <th className="pr-2">Grade</th>
                        <th className="pr-2">Thk</th>
                        <th className="pr-2">Sheet Size</th>
                        <th className="pr-2 text-right">Net Sheets</th>
                        <th className="pr-2 text-right">Weight (Kg)</th>
                        <th className="pr-2 text-right">Shearing Scrap (Kg)</th>
                        <th className="pr-2 text-right">Open Indents</th>
                        <th className="pr-2 text-right">To Procure</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.raw_materials.map((r) => {
                        const canSelect = (r.to_procure_sheets || 0) > 0 && !r.indented;
                        return (
                        <tr key={r.item_id} className="border-b" data-testid={`mrp-rm-${r.unique_id}`}>
                          <td className="py-1.5 pr-2">
                            {r.indented ? (
                              <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[9px] font-semibold" data-testid={`mrp-rm-indented-${r.unique_id}`}>Indented</span>
                            ) : (r.to_procure_sheets || 0) > 0 ? (
                              <Checkbox checked={!!selectedProcure[r.item_id]} onCheckedChange={() => toggleProcure(r.item_id)} data-testid={`mrp-rm-select-${r.unique_id}`} />
                            ) : null}
                          </td>
                          <td className="py-1.5 pr-2 font-medium">{r.item_name}<div className="text-[10px] text-muted-foreground font-mono">{r.unique_id}</div></td>
                          <td className="pr-2">{r.grade || '—'}</td>
                          <td className="pr-2">{r.thickness ?? '—'}</td>
                          <td className="pr-2">{r.sheet_length}×{r.sheet_width}</td>
                          <td className="pr-2 text-right font-mono">{num(r.net_sheets)}</td>
                          <td className="pr-2 text-right font-mono">{num(r.total_weight_kg)}</td>
                          <td className="pr-2 text-right font-mono text-amber-700">{num(r.shearing_scrap_kg)}</td>
                          <td className="pr-2 text-right font-mono">{num(r.open_indent_qty)}</td>
                          <td className="pr-2 text-right font-mono font-bold text-emerald-700">{num(r.to_procure_sheets)}</td>
                        </tr>
                      );})}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
                <span>Total Sheets: <strong className="text-foreground">{num(result.summary?.total_rm_sheets)}</strong></span>
                <span>Total Weight: <strong className="text-foreground">{num(result.summary?.total_rm_weight_kg)} Kg</strong></span>
                <span>Total Shearing Scrap: <strong className="text-amber-700">{num(result.summary?.total_scrap_kg)} Kg</strong></span>
              </div>
            </CardContent>
          </Card>

          {(result.purchased || []).length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Other Purchased Items</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-left border-b text-muted-foreground">
                      <th className="py-1.5 pr-2 w-8"></th>
                      <th className="py-1.5 pr-2">Item</th><th className="pr-2 text-right">Net Qty</th><th className="pr-2">UOM</th>
                      <th className="pr-2 text-right">Open Indents</th><th className="pr-2 text-right">To Procure</th>
                    </tr></thead>
                    <tbody>
                      {result.purchased.map(p => (
                        <tr key={p.item_id} className="border-b">
                          <td className="py-1.5 pr-2">
                            {p.indented ? (
                              <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[9px] font-semibold" data-testid={`mrp-pur-indented-${p.unique_id}`}>Indented</span>
                            ) : (p.to_procure_qty || 0) > 0 ? (
                              <Checkbox checked={!!selectedProcure[p.item_id]} onCheckedChange={() => toggleProcure(p.item_id)} data-testid={`mrp-pur-select-${p.unique_id}`} />
                            ) : null}
                          </td>
                          <td className="py-1.5 pr-2 font-medium">{p.item_name}<div className="text-[10px] text-muted-foreground font-mono">{p.unique_id}</div></td>
                          <td className="pr-2 text-right font-mono">{num(p.net_qty)}</td>
                          <td className="pr-2">{p.uom}</td>
                          <td className="pr-2 text-right font-mono">{num(p.open_indent_qty)}</td>
                          <td className="pr-2 text-right font-mono font-bold text-emerald-700">{num(p.to_procure_qty)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Explosion (all levels)</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-left border-b text-muted-foreground">
                    <th className="py-1.5 pr-2">Item</th><th className="pr-2">Type</th>
                    <th className="pr-2 text-right">Gross</th><th className="pr-2 text-right">On Hand</th>
                    <th className="pr-2 text-right">Committed</th><th className="pr-2 text-right">Available</th>
                    <th className="pr-2 text-right">Net</th><th className="pr-2 text-right">Sheets</th>
                    <th className="pr-2">BOM Used</th>
                  </tr></thead>
                  <tbody>
                    {(result.nodes || []).map(n => {
                      const opts = routesByItem[n.item_id] || [];
                      return (
                      <tr key={n.item_id} className="border-b">
                        <td className="py-1.5 pr-2">{n.item_name}<div className="text-[10px] text-muted-foreground font-mono">{n.unique_id}</div></td>
                        <td className="pr-2">{n.item_type}</td>
                        <td className="pr-2 text-right font-mono">{num(n.gross_qty)}</td>
                        <td className="pr-2 text-right font-mono">{num(n.on_hand)}</td>
                        <td className="pr-2 text-right font-mono">{num(n.committed_stock)}</td>
                        <td className="pr-2 text-right font-mono">{num(n.available_stock)}</td>
                        <td className="pr-2 text-right font-mono font-bold">{num(n.net_qty)}</td>
                        <td className="pr-2 text-right font-mono">{n.sheets_needed != null ? num(n.sheets_needed) : '—'}</td>
                        <td className="pr-2">
                          {n.has_bom && opts.length > 0 ? (
                            <Select value={overrides[n.item_id] || n.route_id || ''} onValueChange={v => handleOverride(n.item_id, v)}>
                              <SelectTrigger className="h-7 text-[11px] w-44" data-testid={`mrp-bom-${n.unique_id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {opts.map(o => (
                                  <SelectItem key={o.id} value={o.id}>{o.route_name}{o.is_default ? ' (default)' : ''}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-[11px] text-muted-foreground">{n.route_name || '—'}</span>
                          )}
                        </td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>
              {(result.warnings || []).length > 0 && (
                <div className="mt-2 text-[11px] text-red-600">{result.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}</div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Saved runs */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><FileSpreadsheet className="w-4 h-4" /> Saved Plans</CardTitle></CardHeader>
        <CardContent>
          {runs.length === 0 ? (
            <p className="text-xs text-muted-foreground py-3 text-center">No saved plans yet.</p>
          ) : (
            <div className="divide-y">
              {runs.map(r => (
                <div key={r.id} className="w-full flex items-center justify-between py-2 hover:bg-accent/40 px-1 rounded group" data-testid={`mrp-run-${r.id}`}>
                  <button onClick={() => openRun(r.id)} className="flex-1 flex items-center justify-between text-left" data-testid={`mrp-run-open-${r.id}`}>
                    <div>
                      <div className="text-xs font-medium">{r.name}</div>
                      <div className="text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleString()} · {r.created_by_name}{r.planning_cycle ? ` · ${r.planning_cycle}` : ''}{r.required_date ? ` · due ${r.required_date}` : ''}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-amber-700 ml-1" onClick={() => copyRun(r.id)} title="Copy plan to a new one" data-testid={`mrp-run-copy-${r.id}`}>
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive ml-1" onClick={() => setDeleteTarget(r)} title="Delete plan" data-testid={`mrp-run-delete-${r.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmConvert} onOpenChange={setConfirmConvert}>
        <AlertDialogContent data-testid="convert-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Convert {selectedIds.length} selected line{selectedIds.length === 1 ? '' : 's'} to indent?</AlertDialogTitle>
            <AlertDialogDescription>
              The selected "to procure" lines will be pushed to the purchase indent pipeline and marked
              as <strong>Indented</strong>, each carrying this plan's own quantity and CTS cutting
              instructions (a new indent line is created even if the same raw material already exists
              from another MRP plan). Choose <strong>Convert &amp; Keep</strong> to leave this plan in
              place so you can review and delete it manually later, or <strong>Convert &amp; Remove</strong>
              to auto-delete the plan once every line is converted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="convert-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleConvertToIndent(false)} className="bg-secondary text-secondary-foreground hover:bg-secondary/80" data-testid="convert-keep-confirm">Convert &amp; Keep Plan</AlertDialogAction>
            <AlertDialogAction onClick={() => handleConvertToIndent(true)} data-testid="convert-confirm">Convert &amp; Remove Plan</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this MRP plan?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.name}" will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteRun} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
